import { useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, resolveUrl } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { useAuth } from "@/context/AuthContext";
import { MaterialIcon } from "@/components/MaterialIcon";
import { btnPrimary, btnSecondary, card, inputCls } from "@/components/admin/ui";

const STEPS = ["Name of Festival", "Dance Styles", "Add Videos"];

const Dots = ({ step }) => (
  <div className="flex items-center justify-center gap-2" data-testid="onboarding-steps">
    {STEPS.map((label, i) => (
      <span key={label} className={`h-2 rounded-full transition-all ${i === step ? "w-8 bg-[#111]" : "w-2 bg-[#D1D5DB]"}`} />
    ))}
  </div>
);

export default function OrganizerOnboarding() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { data: styles } = useApi("/admin/dance-styles");
  const { data: videos } = useApi("/admin/videos");
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [styleIds, setStyleIds] = useState([]);
  const [videoIds, setVideoIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const finishing = useRef(false);

  if (user === null) return <Navigate to="/login" replace />;
  if (user?.onboarded && !finishing.current) return <Navigate to="/admin" replace />;

  const finish = async (withVideos) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/onboarding/festival", {
        name: name.trim(),
        dance_style_ids: styleIds,
        video_ids: withVideos ? videoIds : [],
      });
      finishing.current = true;
      await refreshUser();
      toast.success("Festival created");
      navigate(`/admin/festivals/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  const skipAll = async () => {
    setBusy(true);
    try {
      await api.post("/admin/onboarding/skip");
      finishing.current = true;
      await refreshUser();
      navigate("/admin/festivals");
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F2] py-16 px-5" data-testid="organizer-onboarding">
      <div className={`${card} mx-auto w-full max-w-[640px] px-8 py-10 text-center`}>
        <p className="text-[13px] font-semibold uppercase tracking-wide text-[#8A8A8A]">
          Welcome{user?.organizer_for ? `, ${user.organizer_for}` : ""}
        </p>
        <h1 className="mt-2 text-[26px] font-bold leading-tight md:text-[30px]" data-testid="onboarding-title">
          Create Your First Festival
        </h1>
        <p className="mt-2 mb-7 text-[14px] text-[#6B7280]">
          Step {step + 1} of 3 · {STEPS[step]}
        </p>
        <Dots step={step} />

        {step === 0 && (
          <div className="mt-8 text-left">
            <p className="mb-2 text-[15px] font-semibold">What is your festival called?</p>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && name.trim() && setStep(1)}
              placeholder="e.g. Canadian Swing Championships"
              className={`${inputCls} h-12 text-[16px]`}
              autoFocus
              data-testid="onboarding-name-input"
            />
          </div>
        )}

        {step === 1 && (
          <div className="mt-8 text-left">
            <p className="mb-3 text-[15px] font-semibold">Which dance styles does it feature?</p>
            <div className="flex flex-wrap gap-2" data-testid="onboarding-styles">
              {styles?.map((s) => {
                const on = styleIds.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setStyleIds(on ? styleIds.filter((x) => x !== s.id) : [...styleIds, s.id])}
                    className={`rounded-full border px-4 py-2 text-[14px] font-medium transition-colors ${
                      on ? "border-[#111] bg-[#111] text-white" : "border-[#D1D5DB] bg-white hover:bg-[#F3F4F6]"
                    }`}
                    data-testid={`onboarding-style-${s.slug}`}
                  >
                    {s.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="mt-8 text-left">
            <p className="mb-3 text-[15px] font-semibold">Add videos from your library</p>
            {videos?.length ? (
              <div className="max-h-[260px] divide-y divide-[#E5E7EB] overflow-auto rounded-md border border-[#E5E7EB]" data-testid="onboarding-videos">
                {videos.map((v) => (
                  <label key={v.id} className="flex cursor-pointer items-center gap-3 px-3 py-2 text-[13px] hover:bg-[#F9FAFB]">
                    <input
                      type="checkbox"
                      checked={videoIds.includes(v.id)}
                      onChange={() => setVideoIds(videoIds.includes(v.id) ? videoIds.filter((x) => x !== v.id) : [...videoIds, v.id])}
                      className="accent-[#B644DC]"
                      data-testid={`onboarding-video-${v.id}`}
                    />
                    <div className="h-[32px] w-[56px] shrink-0 overflow-hidden rounded bg-[#E5E7EB]">
                      {v.thumbnail_url && <img src={resolveUrl(v.thumbnail_url)} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <span className="flex-1 truncate">{v.title}</span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="rounded-md border border-dashed border-[#D1D5DB] px-4 py-6 text-center text-[13px] text-[#6B7280]" data-testid="onboarding-no-videos">
                You haven't uploaded any videos yet. You can add them any time from the Videos tab.
              </p>
            )}
          </div>
        )}

        <div className="mt-9 flex items-center justify-between gap-3">
          <button
            type="button"
            className="text-[13px] text-[#6B7280] hover:text-black"
            onClick={() => (step === 0 ? skipAll() : setStep(step - 1))}
            disabled={busy}
            data-testid="onboarding-back-button"
          >
            {step === 0 ? "Skip for now" : "Back"}
          </button>
          <div className="flex items-center gap-2">
            {step === 2 && (
              <button type="button" className={btnSecondary} onClick={() => finish(false)} disabled={busy} data-testid="onboarding-add-later-button">
                Add later
              </button>
            )}
            <button
              type="button"
              className={btnPrimary}
              disabled={busy || (step === 0 && !name.trim())}
              onClick={() => (step === 2 ? finish(true) : setStep(step + 1))}
              data-testid="onboarding-next-button"
            >
              {step === 2 ? (busy ? "Creating…" : "Create Festival") : "Continue"}
              <MaterialIcon name="arrow_circle_right" filled size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
